import {ModalState} from "./state"
import {createModalWithProps} from "./modalTypes"
import {OrError} from "./graphql/errors"
import {InfoModalButton} from "./components/InfoModal"
import * as React from "react"
import {MessageModal} from "./components/MessageModal"
import {fconst, mixed} from "./tsutil"
import {systemErrorMessage} from "./ErrorBoundary"

export class ModalStore {
  static initialState(): ModalState {
    return {
      modal: null,
    }
  }

  constructor(
    private getter: () => ModalState,
    private mapper: (f: (state: ModalState) => ModalState) => void,
  ) {}

  showModal<P>(modalType: React.ComponentType<P>, modalProps: P): void {
    this.mapRoot(
      fconst({
        modal: createModalWithProps(modalType, modalProps),
      }),
    )
  }

  // noinspection JSUnusedLocalSymbols
  handleError(error: mixed): void {
    console.error(error)
    this.showModal(MessageModal, {
      modalStore: this,
      heading: "System Error",
      message: systemErrorMessage,
      button: InfoModalButton.OK,
    })
  }

  handlePromiseThrow(promise: Promise<mixed>): void {
    // Stupid trick to avoid tslint's no-floating-promises and IDEA's unhandled promise
    // inspection at the same time.
    const fun: () => void = async () => {
      try {
        await promise
      } catch (e) {
        this.handleError(e)
      }
    }
    fun()
  }

  handleActionThrow(action: () => Promise<mixed>): void {
    this.handlePromiseThrow(action())
  }

  async applyModal<T>({
    pending,
    ok = () => null,
    error,
    action,
  }: {
    pending?: [string, string]
    ok?: (x: T) => [string, string] | null
    error: (e: string) => [string, string] | null
    action: () => Promise<OrError<T>>
  }): Promise<OrError<T>> {
    const show = ([heading, message]: [string, string], button: InfoModalButton | null) => {
      this.showModal(MessageModal, {modalStore: this, heading, message, button})
    }

    const hide = () => {
      this.hideModal()
    }

    if (pending) {
      show(pending, null)
    }

    // Let any failure bubble up to the root async error handler
    const res = await action()

    const message = res.fold((e) => error(e.message), ok)
    if (message) {
      show(message, res.fold(() => InfoModalButton.Close, () => InfoModalButton.OK))
    } else if (pending) {
      hide()
    }

    return res
  }

  hideModal(): void {
    this.mapRoot(fconst({modal: null}))
  }

  mapRoot(mapper: (state: ModalState) => Partial<ModalState>): void {
    return this.mapper((state) => ({...state, ...mapper(state)}))
  }

  get state() {
    return this.getter()
  }
}



// WEBPACK FOOTER //
// ./src/actions.ts